// [HYDROGRAPHS]: a group's first row (2 tokens) assigns its rain gage; every
// later row is one (month, response type) pair. This app does not model the
// older pre-response-type 3-sets-per-line format.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export const UH_MONTHS = [
  "ALL",
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;
export type UhMonth = (typeof UH_MONTHS)[number];

const UH_RESPONSE_TYPES = ["SHORT", "MEDIUM", "LONG"] as const;
type UhResponseType = (typeof UH_RESPONSE_TYPES)[number];

export interface UnitHydResponse {
  r: number;
  t: number;
  k: number;
  iaMax: number;
  iaRecov: number;
  iaInit: number;
}

export interface UnitHydMonth {
  month: UhMonth;
  short: UnitHydResponse | null;
  medium: UnitHydResponse | null;
  long: UnitHydResponse | null;
}

export interface UnitHydrograph {
  name: string;
  rainGage: string;
  months: UnitHydMonth[];
}

export interface RdiiInflow {
  node: string;
  unitHydrograph: string;
  sewerArea: number;
}

function readResponse(row: string[], warnings?: Set<string>): UnitHydResponse {
  const ctx = (field: string) => `[HYDROGRAPHS] "${row[0]}" ${row[1]} ${row[2]} ${field}`;
  return {
    r: parseNum(row[3], 0, ctx("R"), warnings),
    t: parseNum(row[4], 0, ctx("T"), warnings),
    k: parseNum(row[5], 0, ctx("K"), warnings),
    iaMax: parseNum(row[6], 0, ctx("IA max"), warnings),
    iaRecov: parseNum(row[7], 0, ctx("IA recov"), warnings),
    iaInit: parseNum(row[8], 0, ctx("IA init"), warnings),
  };
}

function writeResponse(
  name: string,
  month: string,
  type: UhResponseType,
  r: UnitHydResponse
): string[] {
  return [
    name,
    month,
    type,
    String(r.r),
    String(r.t),
    String(r.k),
    String(r.iaMax),
    String(r.iaRecov),
    String(r.iaInit),
  ];
}

export function readUnitHydrographs(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): UnitHydrograph[] {
  const rows = raw.get("HYDROGRAPHS") ?? [];
  const order: string[] = [];
  const rowsByName = new Map<string, string[][]>();
  for (const row of rows) {
    const name = row[0];
    if (!name) continue;
    if (!rowsByName.has(name)) {
      rowsByName.set(name, []);
      order.push(name);
    }
    rowsByName.get(name)!.push(row);
  }

  return order.map((name) => {
    const uh: UnitHydrograph = { name, rainGage: "", months: [] };
    const monthOrder: string[] = [];
    const monthsByKey = new Map<string, UnitHydMonth>();
    for (const row of rowsByName.get(name)!) {
      if (row.length === 2) {
        uh.rainGage = row[1] ?? "";
        continue;
      }
      const month = row[1] as UhMonth;
      const type = row[2]?.toUpperCase();
      if (!month || !type || !(UH_RESPONSE_TYPES as readonly string[]).includes(type)) continue;
      if (!monthsByKey.has(month)) {
        monthsByKey.set(month, { month, short: null, medium: null, long: null });
        monthOrder.push(month);
      }
      const entry = monthsByKey.get(month)!;
      if (type === "SHORT") entry.short = readResponse(row, warnings);
      else if (type === "MEDIUM") entry.medium = readResponse(row, warnings);
      else if (type === "LONG") entry.long = readResponse(row, warnings);
    }
    uh.months = monthOrder.map((m) => monthsByKey.get(m)!);
    return uh;
  });
}

export function writeUnitHydrographs(
  raw: ReadonlyMap<string, string[][]>,
  hydrographs: UnitHydrograph[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const uh of hydrographs) {
    if (uh.rainGage) rows.push([uh.name, uh.rainGage]);
    for (const month of uh.months) {
      if (month.short) rows.push(writeResponse(uh.name, month.month, "SHORT", month.short));
      if (month.medium) rows.push(writeResponse(uh.name, month.month, "MEDIUM", month.medium));
      if (month.long) rows.push(writeResponse(uh.name, month.month, "LONG", month.long));
    }
  }
  return writeOrDeleteSection(raw, "HYDROGRAPHS", rows);
}

export function nextAvailableUnitHydName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "UH");
}

export function readRdiiInflows(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): RdiiInflow[] {
  return (raw.get("RDII") ?? [])
    .filter((row) => row[0] && row[1])
    .map((row) => ({
      node: row[0],
      unitHydrograph: row[1],
      sewerArea: parseNum(row[2], 0, `[RDII] "${row[0]}" Sewer Area`, warnings),
    }));
}

export function writeRdiiInflows(
  raw: ReadonlyMap<string, string[][]>,
  inflows: RdiiInflow[]
): Map<string, string[][]> {
  const rows = inflows.map((i) => [i.node, i.unitHydrograph, String(i.sewerArea)]);
  return writeOrDeleteSection(raw, "RDII", rows);
}
